import { Shield, FileText, Lock, Mail, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';

const Datenschutz = () => {
  return (
    <div className="bg-white py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 bg-success/5 px-4 py-2 rounded-lg mb-4">
              <Shield className="w-5 h-5 text-success" />
              <span className="text-success font-medium">Datenschutzerklärung</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Datenschutzerklärung</h1>
            <p className="text-gray-600">
              Stand: {new Date().toLocaleDateString('de-AT', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Table of Contents */}
          <nav className="mb-12 p-6 bg-gray-50 rounded-xl">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-success" />
              Inhaltsverzeichnis
            </h2>
            <ul className="space-y-2">
              {[
                { id: 'allgemein', title: '1. Allgemeine Hinweise' },
                { id: 'verantwortlicher', title: '2. Verantwortlicher' },
                { id: 'datenerfassung', title: '3. Datenerfassung auf unserer Website' },
                { id: 'cookies', title: '4. Cookies und Tracking' },
                { id: 'kontaktform', title: '5. Kontaktformular' },
                { id: 'analyse', title: '6. Analyse-Tools' },
                { id: 'rechte', title: '7. Ihre Rechte' },
                { id: 'sicherheit', title: '8. Datensicherheit' }
              ].map(section => (
                <li key={section.id}>
                  <a 
                    href={`#${section.id}`}
                    className="flex items-center space-x-2 text-gray-600 hover:text-success transition-colors"
                  >
                    <span className="w-1.5 h-1.5 bg-success rounded-full" />
                    <span>{section.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content Sections */}
          <div className="space-y-12">
            {/* Section 1: Allgemeine Hinweise */}
            <section id="allgemein" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">1. Allgemeine Hinweise</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten 
                  passiert, wenn Sie unsere Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie 
                  persönlich identifiziert werden können.
                </p>
                <div className="bg-success/5 rounded-xl p-6 mt-6">
                  <div className="flex items-start gap-4">
                    <Lock className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Datenschutz hat hohe Priorität</h3>
                      <p className="text-gray-600 text-sm">
                        Die Verarbeitung Ihrer personenbezogenen Daten erfolgt ausschließlich im Rahmen der gesetzlichen 
                        Bestimmungen des Datenschutzrechts der Europäischen Union, insbesondere der DSGVO.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Verantwortlicher */}
            <section id="verantwortlicher" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">2. Verantwortlicher</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Verantwortlich für die Datenverarbeitung auf dieser Website ist:
                </p>
                <div className="bg-gray-50 rounded-xl p-6 mt-4">
                  <h3 className="font-semibold mb-4">FPHC UmweltConsulting GmbH</h3>
                  <address className="not-italic text-gray-600">
                    Tischlerbauerstraße 11<br />
                    4060 Leonding / Rufling<br />
                    Österreich<br /><br />
                    Tel: <a href="tel:+436644704858" className="text-success hover:text-dark transition-colors">+43 664 470 48 58</a><br />
                    E-Mail: <a href="mailto:office@fphc.at" className="text-success hover:text-dark transition-colors">office@fphc.at</a>
                  </address>
                </div>
              </div>
            </section>

            {/* Section 3: Datenerfassung */}
            <section id="datenerfassung" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">3. Datenerfassung auf unserer Website</h2>
              <div className="prose prose-gray max-w-none">
                <h3 className="text-xl font-semibold mb-4">Automatische Datenerfassung</h3>
                <p className="text-gray-600 leading-relaxed">
                  Beim Besuch unserer Website werden durch unseren Webserver automatisch Informationen erfasst, die Ihr 
                  Browser an uns übermittelt. Dies sind:
                </p>
                <ul className="mt-4 space-y-2">
                  {[
                    'Browsertyp und -version',
                    'Verwendetes Betriebssystem',
                    'IP-Adresse des zugreifenden Rechners',
                    'Uhrzeit der Serveranfrage'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-2 text-gray-600">
                      <span className="w-1.5 h-1.5 bg-success rounded-full" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Section 4: Cookies */}
            <section id="cookies" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">4. Cookies und Tracking</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Unsere Website verwendet Cookies. Dabei handelt es sich um kleine Textdateien, die Ihr Webbrowser auf 
                  Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher, effektiver und 
                  sicherer zu machen.
                </p>
                <div className="bg-gray-50 rounded-xl p-6 mt-6">
                  <h3 className="font-semibold mb-4">Cookie-Kategorien</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-1.5 h-1.5 bg-success rounded-full mt-2" />
                      <div>
                        <h4 className="font-medium">Technisch notwendige Cookies</h4>
                        <p className="text-sm text-gray-600">Diese sind für den Betrieb der Website erforderlich.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-1.5 h-1.5 bg-success rounded-full mt-2" />
                      <div>
                        <h4 className="font-medium">Analyse-Cookies</h4>
                        <p className="text-sm text-gray-600">Helfen uns, die Nutzung der Website zu verstehen und zu verbessern.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-1.5 h-1.5 bg-success rounded-full mt-2" />
                      <div>
                        <h4 className="font-medium">Marketing-Cookies</h4>
                        <p className="text-sm text-gray-600">Werden verwendet, um personalisierte Werbung anzuzeigen.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5: Kontaktformular */}
            <section id="kontaktform" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">5. Kontaktformular</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem 
                  Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage 
                  und für den Fall von Anschlussfragen bei uns gespeichert.
                </p>
                <div className="bg-success/5 rounded-xl p-6 mt-6">
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Datenverarbeitung im Kontaktformular</h3>
                      <p className="text-gray-600 text-sm">
                        Die Verarbeitung der Daten erfolgt auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO). 
                        Sie können diese Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf 
                        erfolgten Datenverarbeitungsvorgänge bleibt vom Widerruf unberührt.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 8: Analyse Tools */}
            <section id="analyse" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">8. Analyse-Tools</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Zum Zwecke der bedarfsgerechten Gestaltung und fortlaufenden Optimierung unserer Seiten nutzen wir 
                  Analyse-Tools. Mithilfe der dadurch gewonnenen Statistiken können wir unser Angebot verbessern und für 
                  Sie als Nutzer interessanter ausgestalten.
                </p>
              </div>
            </section>

            {/* Section 9: Ihre Rechte */}
            <section id="rechte" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">9. Ihre Rechte</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed mb-6">
                  Sie haben im Rahmen der geltenden gesetzlichen Bestimmungen jederzeit das Recht auf unentgeltliche 
                  Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck 
                  der Datenverarbeitung und ggf. ein Recht auf Berichtigung, Sperrung oder Löschung dieser Daten.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Recht auf Auskunft', desc: 'Sie können eine Bestätigung darüber verlangen, ob personenbezogene Daten verarbeitet werden.' },
                    { title: 'Recht auf Berichtigung', desc: 'Sie haben das Recht, unrichtige oder unvollständige Daten berichtigen zu lassen.' },
                    { title: 'Recht auf Löschung', desc: 'Sie können die Löschung Ihrer Daten verlangen, soweit keine rechtliche Aufbewahrungspflicht besteht.' },
                    { title: 'Recht auf Einschränkung', desc: 'Sie können die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten verlangen.' }
                  ].map((right, index) => (
                    <div key={index} className="bg-gray-50 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{right.title}</h3>
                      <p className="text-sm text-gray-600">{right.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Section 10: Datensicherheit */}
            <section id="sicherheit" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">10. Datensicherheit</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  Wir verwenden innerhalb des Website-Besuchs das verbreitete SSL-Verfahren (Secure Socket Layer) in 
                  Verbindung mit der jeweils höchsten Verschlüsselungsstufe, die von Ihrem Browser unterstützt wird.
                </p>
                <div className="bg-success/5 rounded-xl p-6 mt-6">
                  <div className="flex items-start gap-4">
                    <Shield className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Technische Sicherheitsmaßnahmen</h3>
                      <p className="text-gray-600 text-sm">
                        Um Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, teilweisen oder vollständigen 
                        Verlust, Zerstörung oder gegen den unbefugten Zugriff Dritter zu schützen, setzen wir geeignete 
                        technische und organisatorische Sicherheitsmaßnahmen ein.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer Navigation */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <Link 
                to="/impressum"
                className="text-success hover:text-dark transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                <span>Zum Impressum</span>
              </Link>
              <Link 
                to="/agb"
                className="text-success hover:text-dark transition-colors flex items-center gap-2"
              >
                <Scale className="w-4 h-4" />
                <span>Zu den AGBs</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Datenschutz;